/**
 * Reset de módulos Vuex ao trocar de contexto in-SPA (troca de perfil/escritório).
 *
 * Why: no logout via UI a página é recarregada (`location.href`), o que já
 * libera o heap. O vazamento real em sessões longas vem de trocas de contexto
 * que NÃO recarregam (ex.: `changePerfil` no nav-bar) — os dados do
 * cliente/escritório anterior ficam retidos no store até um reload. Este helper
 * commita a mutation `RESET` (padrão `buildInitialState()`) dos módulos de maior
 * RAM antes do refetch da nova sessão.
 *
 * Allow-list explícita (NÃO usar dispatchActionForAllModules — ele só cobre os
 * módulos core, não os feature registrados via registerModule). Módulos
 * excluídos de propósito: `parameters` (whitelabel/tela de login), `auth`
 * (identidade), `feedback` (diálogos ativos), `websocket` (lifecycle do socket
 * é tratado por $wsWorkspace.disconnect) e dados de referência estáticos
 * (subclasse/instituicoes/municipios). [CU-86ajnmt4r]
 */
// Todos os módulos expõem a mutation `RESET` (controladoria-v2 tem um alias
// `RESET` para o seu `RESET_STATE` pré-existente). Nome único evita a armadilha
// de guard-miss silencioso ao adicionar um módulo com o nome errado.
const RESETTABLE_MODULES = [
  'clientAssets',
  'bi',
  'comissoes',
  'consolidator',
  'crm',
  'financialPlanning',
  'controladoria-v2',
  'ws',
  'wsBackoffice',
]

const RESET_MUTATION = 'RESET'

/**
 * Caches que vivem FORA do Vuex e precisam do mesmo ciclo de vida do reset.
 *
 * O contrato acima commita mutation de módulo — por construção não alcança
 * estado guardado num singleton de API. Cada entrada aqui é um cache assim, com
 * o motivo de existir fora do store. Import dinâmico para não puxar o módulo
 * (e o chunk dele) em quem nunca abriu a tela.
 */
const CACHES_FORA_DO_STORE = [
  // Overlay de operações do WS: campos de conciliação/corretora que o backend
  // ainda não persiste. Sem limpar, a marcação do escritório anterior era
  // mesclada por cima das operações do novo.
  () =>
    import(
      /* webpackChunkName: "ws" */ '@modules/ws/api/apiWsOperations'
    ).then((m) => m.apiWsOperations.limparOverlay()),
  // Janela de repetição do disparo da conciliação: sem limpar, um cliente já
  // visto no escritório anterior ficaria até 5 min sem reconciliar no novo.
  () =>
    import(
      /* webpackChunkName: "ws" */ '@modules/ws/utils/conciliacao-sob-demanda'
    ).then((m) => m.limparHistorico()),
]

export function resetAllModules(store) {
  if (!store || !store._mutations) {
    return
  }
  // Best-effort e assíncrono de propósito: falha ao limpar um cache auxiliar
  // não pode abortar o reset do store, que é o que de fato libera memória.
  CACHES_FORA_DO_STORE.forEach((limpar) => {
    Promise.resolve()
      .then(limpar)
      .catch(() => {})
  })
  RESETTABLE_MODULES.forEach((namespace) => {
    const type = `${namespace}/${RESET_MUTATION}`
    // Guard: módulo pode não estar registrado (lazy) — commit sem checar
    // lançaria "unknown mutation type"
    if (store._mutations[type]) {
      store.commit(type)
    }
  })
}

/**
 * Factory da mutation RESET: reatribui cada chave do state a partir do
 * `buildInitialState()`, reusando a reatividade existente. Centraliza o
 * contrato de reset (antes duplicado em cada módulo). [CU-86ajnmt4r]
 */
export function makeResetMutation(buildInitialState) {
  return (state) => {
    const fresh = buildInitialState()
    Object.keys(fresh).forEach((key) => {
      state[key] = fresh[key]
    })
  }
}

export { RESETTABLE_MODULES, RESET_MUTATION }
